import { FC, useContext, useEffect } from 'react';
import clsx from 'clsx';
import {
  StyledSignInDrawer,
  AccountDrawer,
  StyledAccountPanel,
  StyledUserProfileImageContainer,
} from './SignInDrawer.styles';

import {
  baseVariables,
  Button,
  Heading,
  headingType,
  Icon,
  Label,
  size,
  TypographyColorVariations,
  LinksCollection,
  LinksCollectionVariations,
  Eyebrow,
  EyebrowEnum,
} from '@marriott/mi-ui-library';
import { logger } from '@marriott/mi-headless-utils';
import { getUserDetails, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { useCreateSurveyStore } from '../../store/createSurveyStore';
import { MEMBERPANEL_CONSTANTS, MemberPanelCardLink } from './SignInDrawer.types';
import { PageContext } from '../../context';
import { handleRedirect } from '../../util/helpers';

export const enum UserType {
  MEMBER = 'R',
  SILVER = 'S',
  GOLD = 'G',
  PLATINUM = 'P',
  TITANIUM = 'PPE',
  AMBASADOR = 'PPAE',
}

interface SignInDrawerProps {
  isOpen: boolean;
  togglePanel: () => void;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  modelData: any;
}

export const SignInDrawer: FC<SignInDrawerProps> = ({ isOpen, togglePanel, modelData }) => {
  const pageContext = useContext(PageContext);
  const requestId = pageContext?.requestId || '';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { formData, formValidation, setFormData, isFormValid, validateForm, setFormValidation } =
    useCreateSurveyStore();
  const isSignedInUser = useUserDetailsGlobalStore(state => state?.isSignedInUser);
  const userProfileData = useUserDetailsGlobalStore(state => state);
  const { trips, userProfileSummary, headerSubtext } = userProfileData || {};

  const getImageBorderColor = (): string => {
    const userType = userProfileSummary?.membershipCode as UserType;
    switch (userType) {
      case UserType.SILVER:
        return '#70758d';
      case UserType.GOLD:
        return baseVariables.color['goldSolid'];
      case UserType.PLATINUM:
        return baseVariables.color['platinumSolid'];
      case UserType.TITANIUM:
        return '#8D7474';
      case UserType.AMBASADOR:
        return baseVariables.color['base10'];
      case UserType.MEMBER:
        return baseVariables.color['accent10'];
      default:
        return '';
    }
  };
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  const borderColor = getImageBorderColor();
  const logoutLinkPath = '/mi/phoenix-account-auth/v1/logout';

  // Assume your API response is stored in sessionData

  // prefer root first, fallback to nested if not found
  const redirectingUrl = () => {
    const protocol = window.location.protocol;
    const host = window.location.host;

    // Always force guest-details.mi path
    const pathName = '/my/prearrival.mi';

    // Build URL
    const fullUrlObj = new URL(
      `${protocol}//${host}${pathName}?marshaCode=${formData?.marshaCode}&confirmationNumber=${formData?.confirmationNumber}`
    );

    const fullUrl = fullUrlObj.toString();

    // Redirect
    //window.location.replace(fullUrl);
    handleRedirect(fullUrl);
  };

  const handleLogoutClick = async () => {
    setFormData({
      ...formData,
      isUserEnableSignIn: false,
      isSignInDrawerOpen: false,
    });
    try {
      const response = await fetch(logoutLinkPath, {
        headers: {
          'x-request-id': requestId,
        },
      });
      const { nextStateURI } = await response.json();
      if (nextStateURI) {
        await getUserDetails(true);
        await togglePanel();
        redirectingUrl();
      } else {
        getUserDetails(true);
      }
    } catch (e) {
      const { log } = logger({})('MemberPanel');
      log.error('Logout failed', e);
      // window.location.reload();
    }
  };

  const { MY_TRIPS, BENEFITS } = MEMBERPANEL_CONSTANTS;
  // const memberBenefitsLabel = '{} Benefits';
  // const generateMemberBenefitsLink = () => {
  //   const membershipLevel = userProfileData?.userProfileSummary?.level;

  //   // some benefits have 'Elite' as part and some not, this covers both scenarios to get the user to the right url.
  //   const cleanedMemberStatus = membershipLevel?.split(' ')[0].toLowerCase();
  //   // exp: https://www.marriott.com/loyalty/member-benefits/ambassador.mi
  //   const cardLinkPath = `${MEMBER_BENEFITS_LINK}${cleanedMemberStatus}.mi`;
  //   // we expect "{} Benefits" and this constructs "Titanium Elite Benefits"
  //   const cardLinkLabel = memberBenefitsLabel?.replace('{}', membershipLevel ?? '');

  //   if (membershipLevel && memberBenefitsLabel && cardLinkLabel) {
  //     return getCardLinkData({
  //       cardLinkLabel,
  //       cardLinkPath,
  //       cardLinkId: cardLinkLabel,
  //     });
  //   }
  //   return null;
  // };

  const tripLabel = (tripsLabel: string) =>
    `${tripsLabel}${
      trips?.upcomingReservationsCount && trips?.upcomingReservationsCount > 0
        ? ' (' + trips?.upcomingReservationsCount + ')'
        : ''
    }`;

  const getCardLinkData = (cardLink: MemberPanelCardLink) => {
    return {
      id: (cardLink?.cardLinkId || cardLink?.cardLinkLabel)?.replace(' ', '_'),
      linkLabel: cardLink?.cardLinkLabel,
      linkURL: cardLink?.cardLinkPath,
    };
  };
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const getLinksCollectionData = (cardLinks: MemberPanelCardLink[] | any) => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    let linksCollectionData: any = [];
    if (cardLinks?.length) {
      linksCollectionData = cardLinks.map((linkData: MemberPanelCardLink) => {
        switch (linkData?.cardLinkId) {
          case MY_TRIPS:
            return getCardLinkData({ ...linkData, cardLinkLabel: tripLabel(linkData?.cardLinkLabel) });
          case BENEFITS:
            return getCardLinkData({
              ...linkData,
              cardLinkLabel: `${userProfileSummary?.level} ${linkData?.cardLinkLabel}`,
            });
          default:
            return getCardLinkData(linkData);
        }
      });
    }
    // const memberBenefitsLink = generateMemberBenefitsLink();
    // if (memberBenefitsLink) {
    //   linksCollectionData.push(memberBenefitsLink);
    // }
    return linksCollectionData;
  };
  const signIndrawerLinks = [
    {
      cardLinkLabel: 'Overview',
      clickTrackCardLink: true,
      cardLinkPath: 'https://www.marriott.com/en-gb/loyalty/myAccount/default.mi',
      clickTrackMemberType: false,
    },
    {
      cardLinkLabel: 'Activity',
      clickTrackCardLink: true,
      cardLinkPath: 'https://www.marriott.com/en-gb/loyalty/myAccount/activity.mi',
      clickTrackMemberType: false,
    },
    {
      cardLinkId: 'myTrips',
      cardLinkLabel: 'My Trips',
      clickTrackCardLink: true,
      cardLinkPath: 'https://www.marriott.com/en-gb/loyalty/findReservationList.mi',
      clickTrackMemberType: false,
    },
    {
      cardLinkLabel: 'Favorites',
      clickTrackCardLink: true,
      cardLinkPath: 'https://www.marriott.com/en-gb/loyalty/myAccount/savedHotels.mi',
      clickTrackMemberType: false,
    },
    {
      cardLinkLabel: 'Promotions',
      clickTrackCardLink: true,
      cardLinkPath: 'https://www.marriott.com/en-gb/loyalty/myAccount/promotionCentral.mi',
      clickTrackMemberType: false,
    },
    {
      cardLinkLabel: 'Profile',
      clickTrackCardLink: true,
      cardLinkPath: 'https://www.marriott.com/en-gb/loyalty/myAccount/profile.mi',
      clickTrackMemberType: false,
    },
    {
      cardLinkId: 'benifits',
      cardLinkLabel: 'Benifits',
      clickTrackCardLink: true,
      cardLinkPath: 'https://www.marriott.com/en-gb/loyalty/member-benefits/member.mi',
      clickTrackMemberType: false,
    },
  ];

  const signIndrawerLinksAEM = modelData?.signInModalLinks?.map((item: any) => {
    const obj: any = {
      cardLinkLabel: item.linkTitle,
      clickTrackCardLink: true,
      cardLinkPath: item.linkPath,
      clickTrackMemberType: false,
    };

    // Add cardLinkId for specific cases
    if (item.linkTitle === 'My Trips') obj.cardLinkId = 'myTrips';
    if (item.linkTitle === 'Member Benefits') {
      obj.cardLinkId = 'benefits'; // corrected spelling
      obj.cardLinkLabel = 'Benefits';
    }

    return obj;
  });

  const memberPanelCardLinks = signIndrawerLinksAEM ?? signIndrawerLinks;

  return (
    <StyledSignInDrawer>
      {isOpen && (
        <AccountDrawer isOpen={isOpen} onClick={togglePanel}>
          <StyledAccountPanel
            isOpen={isOpen}
            role="dialog"
            aria-modal="true"
            aria-labelledby="SignIn Drawer"
            onClick={e => e.stopPropagation()}
          >
            <div
              className={clsx({
                'd-flex align-items-center justify-content-between p-0 headerWrapper': !isSignedInUser,
                authenticated: isSignedInUser,
              })}
            >
              <Button
                className="m-icon-button-primary closeButton"
                callback={togglePanel}
                id="closeButton"
                testId="closeButton"
              >
                <Icon iconClass="icon-clear icon-s" ariaLabel="close account panel" />
              </Button>
            </div>

            <div className="d-flex flex-column align-items-center mx-3 mx-lg-5 px-lg-2 signin-drawer">
              <StyledUserProfileImageContainer borderColor={borderColor}>
                <img
                  src={headerSubtext?.image?.src}
                  alt={headerSubtext?.image?.altText ?? ''}
                  className="userprofile-image"
                />
                {userProfileSummary?.lifeTimeLevel && (
                  <Icon iconClass="icon-lifetime d-flex align-items-center justify-content-center p-0" />
                )}
              </StyledUserProfileImageContainer>

              <Label
                variation={TypographyColorVariations.Alternate}
                size={size.small}
                labelText={userProfileSummary?.level ?? ''}
                customClass="mb-0 mt-3"
              />
              <Heading
                fontSize={size.small}
                variation={headingType.title}
                titleText={headerSubtext?.consumerName ?? ''}
                customClass="mb-0 mt-1 m-link-user"
              />

              <Heading
                fontSize={size.extraLarge}
                variation={headingType.subtitle}
                titleText={userProfileSummary?.currentPoints?.toString()}
                customClass={'mb-1 mb-lg-1 mt-3 mt-lg-4 points'}
                data-testid="points"
              />
              <Eyebrow variation={EyebrowEnum.Default} text={'POINTS'} customClass="points" />
              <Button
                buttonCopy={'Buy Points'}
                className={' m-button-s mt-3 mt-lg-4 buypointsbutton'}
                id="buypoints"
                testId="buypointsbutton"
                isLink={true}
                href={'https://storefront.points.com/marriott/en-US/buy?icsid=points_buy_myaccountnear_web_ev'}
                target="_blank"
              />

              <hr className="separator align-self-stretch" />
              <LinksCollection
                linkListData={getLinksCollectionData(memberPanelCardLinks)}
                listCollectionClassname={'listCollection p-0 m-0 align-self-start'}
                linkListClassName={'p-0 m-0  mt-4'}
                listItemClassName={'p-0 m-0 m-nav-link listClassName'}
                variation={LinksCollectionVariations?.NavLink}
              />
              <Button
                className={'m-link-tertiary-button m-0 p-0  mb-5 align-self-start t-overline-overlay signout'}
                id={'signout'}
                callback={handleLogoutClick}
              >
                <span className="signoutLabel">SIGN OUT </span>
              </Button>
            </div>
          </StyledAccountPanel>
        </AccountDrawer>
      )}
    </StyledSignInDrawer>
  );
};
